/**
 * 頁 19 — 「訂單」（供應商收 CoEldery 85 採購單；P1.5-B；`decisions/015` §2.6）
 *
 * 供應商喺呢度：① 睇到 CoEldery 85 落嘅單 ② **確認接單**（可改數量＋填 ETA —— ⚠️ 只准改細、改動要原因）
 * ③ **出貨**（系統自動開一張發貨單，物流照原本流程收貨對數）
 *
 * 紅線
 *   · ⭐ 狀態機／`ackErrors()` 一律由 `@coeldery/purchase-order` 出（前端只做顯示）
 *   · ⚠️ 供應商只見到自己嘅單（後端 `/api/purchase-orders` scope；`§七 D1`）
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormTextField from '@coeldery/form-text-field';
import { btnGhost, btnPrimary, card, errText, hint as hintStyle, input, label } from '@coeldery/ui-kit';
import PoList from '../components/PoList';
import { ApiError, api, type PurchaseDetail, type PurchaseOrder } from '../lib/api';

interface AckLine {
  lineId: string;
  cartonsAcked: string;
}

export default function Orders() {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [detail, setDetail] = useState<PurchaseDetail | null>(null);
  const [expectedAt, setExpectedAt] = useState('');
  const [ackNote, setAckNote] = useState('');
  const [ackLines, setAckLines] = useState<AckLine[]>([]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.purchaseOrders();
      setOrders(res.orders);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** 打開一張單（入去確認／出貨） */
  const open = async (po: PurchaseOrder) => {
    setError(null);
    setNotice(null);
    try {
      const d = await api.purchaseOrder(po.id);
      setDetail(d);
      setExpectedAt(d.order.expectedAt ?? '');
      setAckNote(d.order.ackNote ?? '');
      setAckLines(d.lines.map((l) => ({ lineId: l.id, cartonsAcked: String(l.cartonsAcked ?? l.cartons) })));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  };

  const run = async (action: 'ack' | 'ship') => {
    if (!detail) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (action === 'ack') {
        await api.purchaseAction(detail.order.id, 'ack', {
          expectedAt,
          ackNote: ackNote || null,
          lines: ackLines.map((l) => ({ lineId: l.lineId, cartonsAcked: Number(l.cartonsAcked) })),
        });
      } else {
        await api.purchaseAction(detail.order.id, 'ship');
      }
      setNotice(t(`po.done.${action}`));
      setDetail(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (po: PurchaseOrder, action: 'send' | 'close' | 'cancel' | 'receive') => {
    if (action === 'cancel' && !window.confirm(t('po.ask.cancel'))) return;
    setBusy(true);
    setError(null);
    try {
      await api.purchaseAction(po.id, action);
      setNotice(t(`po.done.${action}`));
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p style={hintStyle}>{t('common.loading')}</p>;

  const nextById = Object.fromEntries(orders.map((o) => [o.id, o.next ?? []]));

  return (
    <>
      <h2 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: 800 }}>{t('orders.title')}</h2>
      <p style={hintStyle}>{t('po.supplierIntro')}</p>

      {error && (
        <p style={errText} role="alert">
          {error}
        </p>
      )}
      {notice && <p style={{ ...hintStyle, fontWeight: 700, color: 'var(--color-primary)' }}>{notice}</p>}

      {/* 確認接單／出貨（打開咗張單先出現） */}
      {detail && (
        <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
          <h3 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 800 }}>{detail.order.poNo}</h3>
          <p style={hintStyle}>{t('po.ackHint')}</p>

          <div style={{ overflowX: 'auto', marginTop: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr>
                  {[t('po.item'), t('po.ordered'), t('po.acked')].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '6px 8px',
                        borderBottom: '1px solid var(--color-divider)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {detail.lines.map((l) => {
                  const acked = ackLines.find((a) => a.lineId === l.id);
                  return (
                    <tr key={l.id}>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-divider)' }}>
                        {l.itemName}（{l.barcode}）
                      </td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-divider)' }}>
                        {l.cartons}
                      </td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-divider)' }}>
                        <input
                          style={{ ...input, width: '90px' }}
                          inputMode="numeric"
                          value={acked?.cartonsAcked ?? ''}
                          onChange={(e) =>
                            setAckLines((prev) =>
                              prev.map((x) => (x.lineId === l.id ? { ...x, cartonsAcked: e.target.value } : x)),
                            )
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '10px' }}>
            <label style={label} htmlFor="po-eta">
              {t('po.eta')}
            </label>
            <input
              id="po-eta"
              type="date"
              style={{ ...input, width: 'auto' }}
              value={expectedAt}
              onChange={(e) => setExpectedAt(e.target.value)}
            />
          </div>
          <div style={{ marginTop: '10px' }}>
            <FormTextField
              id="po-ack-note"
              labelKey="po.ackNote"
              value={ackNote}
              onChange={(v) => setAckNote(String(v))}
            />
          </div>

          <p style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
            {detail.next.includes('ack') && (
              <button type="button" style={btnPrimary} onClick={() => void run('ack')} disabled={busy}>
                {busy ? t('common.saving') : t('po.act.ack')}
              </button>
            )}
            {detail.next.includes('shipped') && (
              <button type="button" style={btnPrimary} onClick={() => void run('ship')} disabled={busy}>
                {t('po.act.ship')}
              </button>
            )}
            <button type="button" style={btnGhost} onClick={() => setDetail(null)}>
              {t('po.close')}
            </button>
          </p>
          {detail.intake && (
            <p style={hintStyle}>{t('po.linkedIntake', { no: detail.intake.batchNo, status: detail.intake.status })}</p>
          )}
        </section>
      )}

      <PoList
        title={t('po.listTitle')}
        emptyText={t('po.emptySupplier')}
        orders={orders}
        nextById={nextById}
        busy={busy}
        onOpen={(po) => void open(po)}
        onAction={(po, action) => void act(po, action)}
      />
    </>
  );
}

